/**
 * SessionSidebar - セッション履歴サイドバー（Claude/ChatGPT風）
 * 
 * SNS認証モード時に表示される左サイドバー。
 * - 閉じた状態: アイコンだけの細いバー（常時表示）
 * - 開いた状態: 280px幅のパネル（メインコンテンツを押す）
 * - ライトテーマ（メインと同系色）
 */
import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlinePlus, HiOutlineChatBubbleLeftRight, HiOutlineTrash } from 'react-icons/hi2';
import { HiOutlineX, HiOutlinePhotograph, HiOutlineSearch } from 'react-icons/hi';
import { TbLayoutSidebarLeftExpand, TbLayoutSidebarLeftCollapse } from 'react-icons/tb';
import './SessionSidebar.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

function SessionSidebar({ isOpen, onToggle, onRestoreSession, onClearRestore, activeSessionId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSessionId, setLoadingSessionId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  // セッション一覧を取得
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const response = await fetch(`${backendUrl}/api/sessions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      } else {
        setFetchError(true);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // サイドバーが開かれたときにセッション一覧を取得
  useEffect(() => {
    if (isOpen) {
      fetchSessions();
    }
  }, [isOpen, fetchSessions]);

  // セッション選択 → メインコンテンツ領域に復元
  const handleSessionClick = async (session) => {
    const sessionMongoId = session.id || session._id;
    if (activeSessionId === sessionMongoId) return;
    
    setLoadingSessionId(sessionMongoId);
    try {
      const response = await fetch(`${backendUrl}/api/sessions/${sessionMongoId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (onRestoreSession) {
          onRestoreSession(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch session detail:', error);
    } finally {
      setLoadingSessionId(null);
    }
  };

  // セッション削除
  const handleDeleteSession = async () => {
    if (!deleteTarget) return;
    try {
      const response = await fetch(`${backendUrl}/api/sessions/${deleteTarget}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setSessions(prev => prev.filter(s => (s.id || s._id) !== deleteTarget));
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
        if (activeSessionId === deleteTarget && onClearRestore) {
          onClearRestore();
        }
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  // 削除確認ダイアログを開く
  const openDeleteConfirm = (e, sessionId) => {
    e.stopPropagation();
    setDeleteTarget(sessionId);
    setShowDeleteConfirm(true);
  };

  // 新規セッション開始
  const handleNewSession = () => {
    if (onClearRestore) {
      onClearRestore();
    }
  };

  // 日付のフォーマット
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60 * 60 * 1000) {
      const minutes = Math.floor(diff / (60 * 1000));
      return `${minutes}分前`;
    }
    if (diff < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(diff / (60 * 60 * 1000));
      return `${hours}時間前`;
    }
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      const days = Math.floor(diff / (24 * 60 * 60 * 1000));
      return `${days}日前`;
    }
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // 検索フィルタ
  const filteredSessions = searchQuery
    ? sessions.filter(s => 
        (s.sessionName || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sessions;

  return (
    <>
      {/* ===== 単一コンテナ: 閉じた時はアイコンバー、開いた時はフルパネル ===== */}
      <div className={`session-sidebar ${isOpen ? 'expanded' : 'collapsed'}`}>
        
        {isOpen ? (
          /* ===== 開いた状態 ===== */
          <>
            {/* サイドバー閉じるボタン */}
            <div className="sidebar-top-row">
              <button
                className="sidebar-close-btn"
                onClick={onToggle}
                title="サイドバーを閉じる"
              >
                <TbLayoutSidebarLeftCollapse size={20} />
              </button>
            </div>

            <div className="sidebar-divider" />

            {/* 新規セッション */}
            <div className="sidebar-new-area">
              <button
                className="sidebar-new-session-btn"
                onClick={handleNewSession}
                title="新規セッション"
              >
                <HiOutlinePlus size={18} />
                <span>新規セッション</span>
              </button>
            </div>

            {/* 検索バー */}
            <div className="sidebar-search-area">
              {showSearch ? (
                <div className="sidebar-search-input-wrapper">
                  <HiOutlineSearch size={16} className="sidebar-search-icon" />
                  <input
                    type="text"
                    className="sidebar-search-input"
                    placeholder="セッションを検索..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                  <button
                    className="sidebar-search-close"
                    onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                  >
                    <HiOutlineX size={14} />
                  </button>
                </div>
              ) : (
                <button
                  className="sidebar-search-toggle"
                  onClick={() => setShowSearch(true)}
                >
                  <HiOutlineSearch size={16} />
                  <span>検索</span>
                </button>
              )}
            </div>

            {/* セッション一覧 */}
            <div className="sidebar-sessions">
              {loading ? (
                <div className="sidebar-loading">
                  <div className="sidebar-loading-spinner" />
                  <span>読み込み中...</span>
                </div>
              ) : fetchError ? (
                <div className="sidebar-error">
                  <p>履歴の読み込みに失敗しました</p>
                  <button className="sidebar-retry-btn" onClick={fetchSessions}>
                    リトライ
                  </button>
                </div>
              ) : filteredSessions.length === 0 ? (
                <div className="sidebar-empty">
                  {searchQuery ? (
                    <p>一致するセッションがありません</p>
                  ) : (
                    <>
                      <HiOutlineChatBubbleLeftRight size={32} className="sidebar-empty-icon" />
                      <p>セッション履歴がありません</p>
                      <p className="sidebar-empty-hint">Librarianの解析完了後に自動保存されます</p>
                    </>
                  )}
                </div>
              ) : (
                filteredSessions.map(session => {
                  const sessionMongoId = session.id || session._id;
                  const isActive = activeSessionId === sessionMongoId;
                  const isLoading = loadingSessionId === sessionMongoId;
                  
                  return (
                    <div
                      key={sessionMongoId}
                      className={`sidebar-session-item ${isActive ? 'active' : ''} ${isLoading ? 'loading' : ''}`}
                      onClick={() => handleSessionClick(session)}
                    >
                      <div className="session-item-content">
                        <div className="session-item-name">
                          {session.sessionName || 'No title'}
                        </div>
                        <div className="session-item-meta">
                          <span className="session-item-date">
                            {formatDate(session.completedAt)}
                          </span>
                          {session.imageUrl && (
                            <span className="session-item-badge" title="画像あり">
                              <HiOutlinePhotograph size={12} />
                            </span>
                          )}
                          {session.sharedStatus?.isPublic && (
                            <span className="session-item-badge" title="公開中" style={{ color: '#059669' }}>
                              公開中
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        className="session-item-delete"
                        onClick={(e) => openDeleteConfirm(e, sessionMongoId)}
                        title="削除"
                      >
                        <HiOutlineTrash size={14} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            {/* ギャラリー/アカウント操作(ログアウト等)は共通ヘッダー(AppHeader)に一本化。
                サイドバーはセッション履歴に専念する。 */}
          </>
        ) : (
          /* ===== 閉じた状態: アイコンバー ===== */
          <div className="sidebar-icon-buttons">
            <button
              className="icon-bar-btn"
              onClick={onToggle}
              title="履歴を開く"
            >
              <TbLayoutSidebarLeftExpand size={20} />
            </button>
            <button
              className="icon-bar-btn"
              onClick={handleNewSession}
              title="新規セッション"
            >
              <HiOutlinePlus size={20} />
            </button>
          </div>
        )}
      </div>

      {/* 削除確認ダイアログ */}
      {showDeleteConfirm && (
        <div className="sidebar-delete-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="sidebar-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>セッションを削除しますか？</h3>
            <p>この操作は取り消せません。</p>
            <div className="sidebar-delete-actions">
              <button 
                className="sidebar-delete-cancel"
                onClick={() => setShowDeleteConfirm(false)}
              >
                キャンセル
              </button>
              <button 
                className="sidebar-delete-ok"
                onClick={handleDeleteSession}
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SessionSidebar;
